/**
 * usePickerMonth — the booking `WorkshopPicker`'s lifted calendar month state
 * (BON-142). The month nav lives in the FIXED chrome row above the scrolling
 * grid (not the shared `WorkshopCalendarPicker`'s own internal header, which
 * used to scroll out of view with it), so the picker owns and passes the month
 * down as a controlled prop.
 *
 * Defaults to the CURRENT calendar month. The fetch is scoped to whichever
 * month is shown (see `useMonthWorkshops`), so opening on "now" keeps the
 * initial payload to a single month; the user pages with the nav to reach
 * later months.
 */
import { useState } from 'react';

/**
 * `anchor` is the month to OPEN on. Rebooking passes the customer's existing
 * workshop date: the fetch is month-scoped, so opening on today hides the source
 * workshop whenever it is in another month — which is most of the time — and
 * with it the "Current" chip a CSR uses to judge how far the new date moves the
 * customer. Creating a booking has no such anchor and keeps today.
 */
export function usePickerMonth(anchor?: string | Date | null) {
  const [month, setMonth] = useState<Date>(() => {
    if (!anchor) return new Date();
    const parsed = anchor instanceof Date ? anchor : new Date(anchor);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  });
  return { month, setMonth };
}
